import { AbstractGoodgameClip } from "./AbstractGoodgameClip";

import { debug } from "../../logging/debug";
import { fatal } from "../../logging/fatal";
import { GoodgameBitmapEngine } from "../GoodgameBitmapEngine";
import { FlattenedImage } from "../clipsource/FlattenedImage";
import { FrameStrip } from "../clipsource/FrameStrip";
import { IClipSource } from "../clipsource/IClipSource";
import { ClipSizeComponent } from "../components/ClipSizeComponent";
import { InteractiveBitmapClipComponent } from "../components/InteractiveBitmapClipComponent";

import Bitmap = createjs.Bitmap;
import Event = createjs.Event;
import Point = createjs.Point;

/**
 *    plays and visualizes a framstrip with bitmapdata like MovieClip with embedded assets
 */
export class GoodgameBitmapClip extends AbstractGoodgameClip {


    /** @private container for bitmapdata. */
    // TODO: @HTML5 - added empty string to the constructor because createjs Bitmap constructor signature is different
    protected _bmpImage: Bitmap = new Bitmap("");

    /** container for bitmapdata. */
    public get bmpImage(): Bitmap {
        return this._bmpImage;
    }

    public set bmpImage(bm: Bitmap) {
        this._bmpImage = bm;
    }

    /** @private Source of the BitmapImages */
    protected source: IClipSource;

    /** if you set this callback, it will be executed after frame change. Used for caching! **/
    public onFrameChangedCallback: Function;

    /** returns the current frames of bitmapclip*/
    protected get frameStrip(): FrameStrip {
        if (this.source) {
            return this.source.frameStrip;
        } else {
            return null;
        }
    }

    /** BitmapData of the current Frame */
    protected currentFrameImage: Bitmap;

    /** current index of frame in frameCache. */
    protected frameIndex: number;

    /** component for mouse-interaction */
    protected _interactiveComponent: InteractiveBitmapClipComponent;

    /** component for mouse-interaction */
    public get interactiveComponent(): InteractiveBitmapClipComponent {
        return this._interactiveComponent;
    }

    public autoDispose: boolean = true;

    /** remove methods from signals etc **/
    public dispose(): void {
        if (this._clipSizeComponent) {
            this._clipSizeComponent.destroy();
        }
        if (this._interactiveComponent) {
            this._interactiveComponent.dispose();
        }
        this._copiedBitmapFrame.forEach( bitmap => {
            if(bitmap && bitmap.parent) {
                bitmap.parent = null;
            }
        });
        if(this._copiedBitmapFrame.length) this._copiedBitmapFrame = [];
    }

    public set clipSizeComponent(clipSize: ClipSizeComponent) {
        this._clipSizeComponent = clipSize;

        if (this._clipSizeComponent) {
            this._clipSizeComponent.setImageSize(this, this.source.clipBounds);
            this._clipSizeComponent.clipSizeChanged.dispatch(this);
        }
    }

    /** enable or disable mouse interaction for this component */
    private _isInteractive: boolean = false;

    /** enable or disable mouse interaction for this component */
    public get isInteractive(): boolean {
        return this._isInteractive;
    }

    /** enable or disable mouse interaction for this component */
    public set isInteractive(value: boolean) {
        if (!value && this._interactiveComponent) {
            this._interactiveComponent.dispose();
            this._interactiveComponent = null;
        }
        this._isInteractive = value;
        this.mouseChildren = false;
        this.mouseEnabled = this._isInteractive;
    }

    /**
     * store a list of bitmaps which cloned from framestrip
     */
    private _copiedBitmapFrame: Bitmap[] = [];

    /**
     * Constructor
     *
     * @param src source for the animation
     * @param interactiveClip flag if clip receives mouse events
     * @param fps clip will be played with given fps
     * @param autoplay flag if clip will be started on creation
     *
     */
    constructor(src: IClipSource, fps: number = 24, autoPlay: boolean = true) {
        super();
        this.animationManager = GoodgameBitmapEngine.instance.animationManager;
        this.mouseChildren = false;
        this.mouseEnabled = false;
        this.source = src;
        this.targetFps = fps;
        this._running = autoPlay;
        this._startFrame = 1;
        // this.addChild(this.bmpImage);
        this.addEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(this.handleRemovedFromStage));

        if (this.clipSizeComponent) {
            this.clipSizeComponent.setImageSize(this, this.source.clipBounds);
        }
    }

    protected init(): void {
        if (this.frameStrip) {
            this._endFrame = this.frameStrip.frameAmount;

            if (this.frameStrip.hasMultipleFrames && this._running) {
                this.play();
            } else {
                this.gotoAndStop(1);
            }
        }
    }

    private static BASE_POINT: Point = new Point();
    private static HIT_TEST_POINT: Point = new Point();

    public isHit(posX: number, posY: number): boolean {

        let pt: Point = new Point(posX, posY);
        pt = this.bmpImage.globalToLocal(pt.x, pt.y);

        GoodgameBitmapClip.HIT_TEST_POINT.x = pt.x;
        GoodgameBitmapClip.HIT_TEST_POINT.y = pt.y;

        // TODO: @HTML5
        /*if (this.bmpImage.bitmapData != null) {
            return this.bmpImage.bitmapData.hitTest(GoodgameBitmapClip.BASE_POINT, 0, GoodgameBitmapClip.HIT_TEST_POINT);
        }
        else {
            return false;
        }*/

        // TODO: @HTML5 - remove this after fixing
        return false
    }

    private handleRemovedFromStage(event: Event): void {
        if (this.autoDispose) {
            this.dispose();
        }
        this.stop();
        this.removeEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(this.handleRemovedFromStage));
    }

    protected jumpToFrame(frame: number): void {
        if (!this.frameStrip) {
            return;
        }

        if (this.frameStrip && frame > 0) {
            let framechanged: boolean = this._currentFrame !== frame || !this.currentFrameImage;
            this._currentFrame = frame;
            this.frameIndex = this._currentFrame - 1;
            this.currentFrameImage = this.frameStrip.getFrame(Math.min(this.frameIndex, this.frameStrip.frameAmount - 1));

            if (!this.currentFrameImage) {
                return;
            }

            // TODO: @HTML5
            this._bmpImage = this.currentFrameImage;
            this.bmpImage.smoothing = GoodgameBitmapEngine.instance.smoothing;
            this.bmpImage.pixelSnapping = GoodgameBitmapEngine.instance.pixelSnapping;
            // clear children
            this.children.splice(0, 1);
            if(!this.bmpImage.parent){
                this.children.push(this.bmpImage);
                this.bmpImage.parent = this;
            } else {
                // try to reuse canvas, need to clone bitmap
                if(this.bmpImage.parent != this) {
                    if(this._copiedBitmapFrame[frame]) {
                        this.children.push(this._copiedBitmapFrame[frame]);
                    } else {
                        let bitmapClone = this.bmpImage.clone();
                        bitmapClone.parent = this;
                        this.children.push(bitmapClone);
                        if(frame > 1 || this._copiedBitmapFrame.length > 2) {
                            // cache it when larger than 1 frame, bypass isoObject not clickable for the second time
                            this._copiedBitmapFrame[frame] = bitmapClone;
                        }
                    }
                } else {
                    this.children.push(this.bmpImage);
                }
            }
            // this.bmpImage.x = (0 - this.currentFrameImage.registrationPointX);
            // this.bmpImage.y = (0 - this.currentFrameImage.registrationPointY);

            if ( framechanged && this.onFrameChangedCallback != null ) {
                this.onFrameChangedCallback();
            }
        } else {
            if (frame === 0) {
                return;
            }
            let errorString: string = "cannot jump to frame. invalid frame number: " + frame;
            fatal(errorString);
        }
    }

    public traceDebugLog(): void {
        debug("GoodgameBitmapClip traceDebugLog+++++++++++++++++++++++++++++++");
        debug("\t\t contains( bmpImage )=" + this.contains(this.bmpImage));
        debug("\t\t animationManager=" + this.animationManager);
        debug("\t\t mouseChildren=" + this.mouseChildren);
        debug("\t\t source=" + this.source);
        debug("\t\t _running=" + this._running);
        debug("\t\t _startFrame=" + this._startFrame);
        debug("\t\t _currentFrame=" + this._currentFrame);
        debug("\t\t frameIndex=" + this.frameIndex);
        debug("\t\t currentFrameImage=" + this.currentFrameImage);
        debug("\t\t targetFps=" + this._tagetFps);
        debug("\t\t\t\t source.cacheId=" + this.source.cacheId);
        debug("\t\t\t\t source.clipBounds=" + this.source.clipBounds);
        debug("\t\t\t\t source.colorID=" + this.source.colorID);
        debug("\t\t\t\t source.layerInfo=" + this.source.layerInfo);
        debug("\t\t\t\t source.scaleFactor=" + this.source.scaleFactor);
        debug("\t\t\t\t tsource.cacheId=" + this.source.cacheId);
        debug("\t\t\t\t source.frameStrip=" + this.source.frameStrip);
        debug("\t\t\t\t\t\t source.frameStrip.frameAmount=" + this.source.frameStrip.frameAmount);
    }
}
